// ---------------------------------------------------------
// Datei: PhaseScreen.tsx
// Zweck: Anzeige und Verwaltung einer Projektphase mit Dokumenten
// Besonderheiten:
// - Verwendet DocumentChecklist für Anzeige der Dokumente
import { getSharedDriveName } from "../utils/env";
// - Sticky Header mit Progress-Bar
// - CaptureModal für Dokumenten-Upload
// - Mobile-First mit Safe-Areas
// - Zentraler Document-Store für State-Persistenz über Navigation
// - ThemeToggle für Light/Dark Mode (Sprint 2 Task 1)
// - NEU: Make.com Webhook Upload für Arbeitsberichte (nur Bericht-Phase)
// Stand: 2026-10-05
// ---------------------------------------------------------

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueue } from '../contexts/QueueContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getDealByIdAsync as getDealById, updateTextPropertyAsync as updateDealTextProperty } from '../services/api/hubspot';
import { isAuthMissingError } from '../services/api/one360';
import { useDealsStore } from '../store/dealsStore';
import { mapDealToPhases } from '../services/hubspotDealMapper';
import CaptureModal from '../components/CaptureModal';
import InbetriebnahmeStatus from '../components/InbetriebnahmeStatus';
import SyncStatus from '../components/SyncStatus';
import DocumentChecklist from '../components/DocumentChecklist';
import { DealContact, HubSpotDeal } from '../types/hubspot';
import { useDocumentStore } from '../store/documentState';
import { DocumentType, Phase } from '../types/documents';
import type { DocumentUploadQueueData, HubspotTextPropertyQueueData, VaillantSerialRegisterQueueData } from '../interfaces/queue';
import FileUploadModal, { UploadFile } from '../components/FileUploadModal';
import { stripUploadedDocumentFilesRuntimeData } from '../utils/uploadedDocumentFiles';
import AppHeader from '../components/AppHeader';
// NEU: Make.com Webhook Upload Komponente (NUR für Arbeitsberichte)
// NEU: Allgemeine Uploads (Issue #62) – generischer Foto-Upload ohne Phasenzuordnung
import AllgemeineUploads from '../components/AllgemeineUploads';
// NEU: Störungen-Formular – Foto + Text → One360-API → HubSpot-Ticket
import StoerungenFormular from '../components/StoerungenFormular';
import AbschlussberichtWizard from '../components/AbschlussberichtWizard';
import MaintenanceProtocols from '../components/MaintenanceProtocols';
import ProjektdokumentationWizard from '../components/ProjektdokumentationWizard';
import ProjektvorbereitungBriefing from '../components/ProjektvorbereitungBriefing';
import SeriennummerModal from '../components/SeriennummerModal';
import SeriennummerRegistrierenModal from '../components/SeriennummerRegistrierenModal';
import PhaseFooterNav from '../components/PhaseFooterNav';
import { registerVaillantSerialNumber } from '../services/api/vaillant';
import { QueueItemLockTimeoutError } from '../services/queueService';
import { validateVaillantSerialNumber, getKnownAusseneinheitSerialNumber as getKnownAusseneinheitSerialNumberUtil } from '../utils/vaillantSerialNumber';

const parseLegacyObjectAddress = (value: string | null | undefined): {
  address?: string;
  zip: string;
  city: string;
} => {
  const sanitized = String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const match = sanitized.match(/^(.*?)(?:,\s*)?(\d{5})\s+(.+)$/);

  return {
    address: match?.[1].trim() || sanitized || undefined,
    zip: match?.[2] || '',
    city: match?.[3].trim() || '',
  };
};

/** Extrahiert für ein Log den aussagekräftigsten Teil eines unbekannten Fehlers (Axios-Response-Body bevorzugt vor der Error-Message). */
const describeApiError = (err: unknown): unknown => {
  if (err && typeof err === 'object') {
    const responseData = (err as { response?: { data?: unknown } }).response?.data;
    if (responseData !== undefined) return responseData;
    if (err instanceof Error) return err.message;
  }
  return err;
};

// Code-Review-Finding (Re-Review, zu #1199): vaillantEnqueueFailure (siehe unten) war bisher
// reiner React-State - ein Reload/App-Neustart genau nach dem addToQueue-Fehlschlag, den
// dieser Marker festhalten soll, hätte den Marker mitgelöscht und den ambivalenten Zustand
// unbemerkt verschwinden lassen. localStorage ist ein von der (in diesem Fehlerfall
// mutmaßlich erschöpften) IndexedDB-Queue getrennter Speicher mit eigenem Quota - für die
// paar Bytes dieses Markers (kein Foto-Payload) bleibt hier typischerweise noch Platz, auch
// wenn addToQueue wegen der viel größeren Base64-Bilddaten an IndexedDB-Quota scheitert.
// Kein Ersatz für eine serverseitig idempotente Registrierung (siehe Backend-Ticket), aber
// überlebt zumindest einen Reload, statt bei jedem Neuladen der Seite spurlos zu verschwinden.
const vaillantEnqueueFailureStorageKey = (dealId: string) => `vaillant-enqueue-failure:${dealId}`;

const readVaillantEnqueueFailure = (dealId: string): VaillantSerialRegisterQueueData | null => {
  try {
    const raw = window.localStorage.getItem(vaillantEnqueueFailureStorageKey(dealId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.dealId === dealId) {
      return parsed as VaillantSerialRegisterQueueData;
    }
    return null;
  } catch (error) {
    console.warn('[SeriennummerRegistrieren] Ambiguitätsmarker konnte nicht gelesen werden:', error);
    return null;
  }
};

const writeVaillantEnqueueFailure = (data: VaillantSerialRegisterQueueData): void => {
  try {
    window.localStorage.setItem(vaillantEnqueueFailureStorageKey(data.dealId), JSON.stringify(data));
  } catch (error) {
    // Beide Speicher (IndexedDB-Queue UND localStorage) sind erschöpft - von hier aus nicht
    // mehr lösbar. Der Banner bleibt für die aktuelle Session trotzdem sichtbar (React-State),
    // ein Reload wäre in diesem doppelten Ausnahmefall aber nicht mehr geschützt.
    console.error('[SeriennummerRegistrieren] Ambiguitätsmarker konnte auch nicht in localStorage gesichert werden:', error);
  }
};

const clearVaillantEnqueueFailure = (dealId: string): void => {
  try {
    window.localStorage.removeItem(vaillantEnqueueFailureStorageKey(dealId));
  } catch (error) {
    console.warn('[SeriennummerRegistrieren] Ambiguitätsmarker konnte nicht entfernt werden:', error);
  }
};

// Code-Review-Finding (Re-Review): schlägt removeFromQueue NACH einer bereits erfolgreichen
// Live-Registrierung fehl, bleibt das veraltete vaillant_serial_register-Item weiterhin in
// der Queue - der "Jetzt erneut versuchen"-Banner bliebe bedienbar und confirmPendingItem
// würde registerVaillantSerialNumber ein drittes Mal aufrufen. Dieser Marker macht so ein
// Item dauerhaft (auch über einen Reload hinweg) als "bereits erledigt" erkennbar, damit die
// Bestätigung zu einem sicheren No-Op wird, statt Vaillant erneut zu kontaktieren - unabhängig
// davon, ob die eigentliche Löschung je gelingt.
const vaillantObsoleteItemStorageKey = (dealId: string) => `vaillant-obsolete-item:${dealId}`;

const readVaillantObsoleteItemId = (dealId: string): string | null => {
  try {
    return window.localStorage.getItem(vaillantObsoleteItemStorageKey(dealId));
  } catch (error) {
    console.warn('[SeriennummerRegistrieren] Obsolet-Markierung konnte nicht gelesen werden:', error);
    return null;
  }
};

const writeVaillantObsoleteItemId = (dealId: string, itemId: string): void => {
  try {
    window.localStorage.setItem(vaillantObsoleteItemStorageKey(dealId), itemId);
  } catch (error) {
    console.error('[SeriennummerRegistrieren] Obsolet-Markierung konnte nicht gesichert werden:', error);
  }
};

const clearVaillantObsoleteItemId = (dealId: string): void => {
  try {
    window.localStorage.removeItem(vaillantObsoleteItemStorageKey(dealId));
  } catch (error) {
    console.warn('[SeriennummerRegistrieren] Obsolet-Markierung konnte nicht entfernt werden:', error);
  }
};

const PhaseScreen: React.FC = () => {
  // URL-Parameter extrahieren
  const { phaseId, projectId } = useParams<{ phaseId: string; projectId: string }>();
  const navigate = useNavigate();
  const { addToQueue, items: queueItems, confirmPendingItem, removeFromQueue } = useQueue();
  const { logout } = useAuth();
  const { showToast } = useToast();
  
  // Document-Store für persistenten Zustand
  const { updateDocumentStatus, getPhaseProgress, updatePhaseStatus, saveDocumentData, getDocumentFiles } = useDocumentStore();
  const storedPhaseStatus = useDocumentStore((state) =>
    phaseId ? state.phases[phaseId] : undefined
  );

  // Deals aus dem persistierten Store (offline-fähig, kein erneuter API-Aufruf bei Navigation)
  const { upsertDeal } = useDealsStore();
  
  // State-Definitionen
  const [phase, setPhase] = useState<Phase | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCaptureModal, setShowCaptureModal] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentType | null>(null);
  const [completing, setCompleting] = useState<boolean>(false);
  const [reloadKey, setReloadKey] = useState<number>(0);
  const [completionCountdown, setCompletionCountdown] = useState<number | null>(null);
  const [completionNavTarget, setCompletionNavTarget] = useState<string | null>(null);
  const prevProgressCompleteRef = useRef(false);
  // Code-Review-Finding (P1): serialisiert den Live-Registrierungsversuch
  // (handleSeriennummerRegistrierenSubmit) und die Banner-Bestätigung
  // (handleConfirmVaillantRegistration) gegeneinander - ohne diesen gemeinsamen Guard laufen
  // beide Einstiegspfade unabhängig voneinander (der direkte POST setzt den Queue-
  // `isProcessing`-Zustand nicht), wodurch zwei nicht-idempotente Vaillant-Requests parallel
  // starten könnten.
  const vaillantRegistrationInFlightRef = useRef(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState<boolean>(false);
  // NEU: Deal-Daten für Webhook Upload (für Bericht-Phase und VOT-Phase relevant)
  const [activeDeal, setActiveDeal] = useState<HubSpotDeal | null>(null);
  const dealName = activeDeal?.dealname || '';
  const gdriveFolderId = activeDeal?.gdrive_folder_id || undefined;
  const [dealContact, setDealContact] = useState<DealContact | undefined>(undefined);
  const maintenanceProjectDetails = useMemo(() => {
    const contactName = [dealContact?.firstname, dealContact?.lastname].filter(Boolean).join(' ') || undefined;
    // Die Anlagenadresse gehört zum Deal und nicht zum zugehörigen Kontakt.
    const legacyObjectAddress = parseLegacyObjectAddress(activeDeal?.objekt___anschrift);
    const objectAddress = activeDeal?.objekt___adresse?.trim() || legacyObjectAddress.address;
    return {
      projectName: dealName || undefined,
      contactName,
      contactPhone: dealContact?.phone,
      contactMobilePhone: dealContact?.mobilephone,
      contactEmail: dealContact?.email,
      address: objectAddress,
      zip: activeDeal?.objekt___plz?.trim() || legacyObjectAddress.zip,
      city: activeDeal?.objekt___stadt?.trim() || legacyObjectAddress.city,
    };
  }, [
    activeDeal?.objekt___adresse,
    activeDeal?.objekt___anschrift,
    activeDeal?.objekt___plz,
    activeDeal?.objekt___stadt,
    dealContact,
    dealName,
  ]);
  const [showSeriennummerModal, setShowSeriennummerModal] = useState<boolean>(false);
  const [showSeriennummerRegistrierenModal, setShowSeriennummerRegistrierenModal] = useState<boolean>(false);
  const [seriennummerRegistrierenInitialValue, setSeriennummerRegistrierenInitialValue] = useState<string>('');
  const [seriennummerAusseneinheitInitialValue, setSeriennummerAusseneinheitInitialValue] = useState<string>('');
  const [reopenRegistrierenAfterAusseneinheit, setReopenRegistrierenAfterAusseneinheit] = useState<boolean>(false);
  const [confirmingVaillantRegistration, setConfirmingVaillantRegistration] = useState<boolean>(false);
  // Code-Review-Finding (Re-Review): hält den ambivalenten Zustand sichtbar, wenn selbst das
  // Vormerken des Fallback-Items (addToQueue) fehlschlägt (z.B. Speicherplatz voll) - ohne
  // dieses In-Memory-Backup gäbe es GAR KEIN Pending-Item, das den Zustand repräsentiert, und
  // ein Monteur könnte sofort erneut live absenden, obwohl der ursprüngliche Versuch weiterhin
  // ambivalent ist (Vaillant könnte ihn bereits verarbeitet haben).
  const [vaillantEnqueueFailure, setVaillantEnqueueFailure] = useState<VaillantSerialRegisterQueueData | null>(null);
  const [retryingVaillantEnqueue, setRetryingVaillantEnqueue] = useState<boolean>(false);
  // Code-Review-Finding (Re-Review): ID eines vaillant_serial_register-Items, das trotz
  // erfolgreicher Live-Registrierung nicht aus der Queue entfernt werden konnte - macht eine
  // spätere Bestätigung dieses Items zu einem sicheren No-Op statt Vaillant erneut zu
  // kontaktieren (siehe handleConfirmVaillantRegistration).
  const [vaillantObsoleteItemId, setVaillantObsoleteItemId] = useState<string | null>(null);
  // Alle Phasen des aktuellen Projekts (für Footer-Direktnavigation, Issue #16)
  const [allPhases, setAllPhases] = useState<Array<{ _id: string; name: string; status?: string; completionPercent?: number }>>([]);

  // Phase laden
  useEffect(() => {
    let unmounted = false;
    const fetchPhase = async () => {
      if (!phaseId) {
        navigate('/projects');
        return;
      }

      try {
        setLoading(true);

        // Prüfe ob es eine HubSpot-Phase ist (Format: dealId-phaseName)
        const isHubSpotPhase = phaseId.includes('-') && (
          phaseId.endsWith('-projektvorbereitung') ||
          phaseId.endsWith('-projektdokumentation') ||
          phaseId.endsWith('-durchfuehrung') ||
          phaseId.endsWith('-inbetriebnahme') ||
          phaseId.endsWith('-abschlussbericht') ||
          phaseId.endsWith('-uploads') ||
          phaseId.endsWith('-stoerungen') ||
          phaseId.endsWith('-service')
        );
        
        if (isHubSpotPhase) {
          // HubSpot-Phase: Lade Deal-Daten aus Cache (kein API-Aufruf bei Navigation)
          const dealId = phaseId.split('-')[0];
          const deals = useDealsStore.getState().deals;
          let deal: HubSpotDeal | null | undefined = deals.find(d => d.id === dealId);
          if (!deal || !('vaillant_api_consent_angefragt' in deal) || !('contact' in deal)) {
              const fetchedDeal = await getDealById(dealId);
            if (fetchedDeal) {
              deal = fetchedDeal;
              upsertDeal(fetchedDeal);
            }
          }
          
          if (!deal) {
            throw new Error('Projekt nicht gefunden');
          }


          // Mappe Deal zu Phasen und finde die gewünschte Phase
          const phases = mapDealToPhases(deal);
          // Alle Phasen für Footer-Direktnavigation merken (Issue #16)
          if (!unmounted) {
            setAllPhases(phases.map(p => ({ _id: p._id, name: p.name, status: p.status, completionPercent: p.completionPercent })));
          }
          const targetPhase = phases.find(p => p._id === phaseId);
          
          if (!targetPhase) {
            throw new Error('Phase nicht gefunden');
          }
          
          if (unmounted) return;
          
          const latestStoredPhaseStatus = useDocumentStore.getState().phases[phaseId];

          // Setze Phase-Daten (HubSpot-Format ist kompatibel mit Phase-Interface)
          setPhase({
            _id: targetPhase._id,
            name: targetPhase.name,
            description: targetPhase.description,
            status: ((latestStoredPhaseStatus?.status || targetPhase.status) === 'completed' ? 'completed' : 'pending') as 'pending' | 'completed',
            project: targetPhase.project,
            completedAt: latestStoredPhaseStatus?.completedAt || targetPhase.completedAt,
            completedByEmail: phaseId.endsWith('-projektdokumentation')
              ? targetPhase.completedByEmail
              : latestStoredPhaseStatus?.completedByEmail || targetPhase.completedByEmail,
            approvedByEmail: targetPhase.approvedByEmail,
          });
          
          // NEU: Deal-Daten für Webhook Upload speichern
          setActiveDeal(deal);
          // Immer setzen (auch wenn undefined) — sonst bleibt bei einem Deal-Wechsel
          // ohne eigenen Kontakt der Kontakt des vorherigen Deals im State hängen,
          // da dieselbe PhaseScreen-Instanz bei einem phaseId-Wechsel weiterlebt.
          setDealContact(deal.contact);
          
          // WICHTIG: Store befüllen BEVOR DocumentChecklist rendert
          const { extractDocumentStatusWithTypes, calculatePhaseProgress } = await import('../services/hubspotDealMapper');
          const documents = extractDocumentStatusWithTypes(deal, phaseId);
          const progress = calculatePhaseProgress(phaseId, deal);
          
          console.log('[PhaseScreen] Befülle Store mit', documents.length, 'Dokumenten für', phaseId);
          const { useDocumentStore: documentStore } = await import('../store/documentState');
          const { setHubSpotDocumentStatus } = documentStore.getState();
          setHubSpotDocumentStatus(phaseId, documents, progress);
        }
      } catch (err: any) {
        if (unmounted) return;
        // Abgelaufener Token beim Phasenwechsel: AuthContext leitet parallel über
        // auth:unauthorized/AuthGuard zu /login um. Ohne diesen Guard würde hier kurz
        // "Kein Auth-Token verfügbar" statt der Login-Weiterleitung aufblitzen.
        if (isAuthMissingError(err)) return;
        setError(err.message || 'Fehler beim Laden der Phase');
        console.error('Fehler beim Laden der Phase:', err);
      } finally {
        if (!unmounted) setLoading(false);
      }
    };

    fetchPhase();
    return () => {
      unmounted = true;
    };
  }, [phaseId, projectId, navigate, reloadKey, upsertDeal]);

  useEffect(() => {
    if (!storedPhaseStatus || !phase) {
      return;
    }

    const isCurrentProjektdokumentationPhase = phaseId?.endsWith('-projektdokumentation') || false;

    if (
      phase.status !== storedPhaseStatus.status ||
      phase.completedAt !== storedPhaseStatus.completedAt ||
      (!isCurrentProjektdokumentationPhase && phase.completedByEmail !== storedPhaseStatus.completedByEmail)
    ) {
      setPhase((previous) =>
        previous
          ? {
              ...previous,
              status: storedPhaseStatus.status,
              completedAt: storedPhaseStatus.completedAt,
              completedByEmail: isCurrentProjektdokumentationPhase
                ? previous.completedByEmail
                : storedPhaseStatus.completedByEmail,
            }
          : previous
      );
    }
  }, [phase, phaseId, storedPhaseStatus]);

  // Phase-Typ-Flags und Progress – werden von useEffects und JSX genutzt
  const isProjektvorbereitungPhase = phaseId?.endsWith('-projektvorbereitung') || false;
  const isAbschlussberichtPhase = phaseId?.endsWith('-abschlussbericht') || false;
  const isProjektdokumentationPhase = phaseId?.endsWith('-projektdokumentation') || false;
  const isDurchfuehrungPhase = phaseId?.endsWith('-durchfuehrung') || false;
  const isInbetriebnahmePhase = phaseId?.endsWith('-inbetriebnahme') || false;
  const isUploadsPhase = phaseId?.endsWith('-uploads') || false;
  const isStoerungenPhase = phaseId?.endsWith('-stoerungen') || false;
  const isMaintenancePhase = phaseId?.endsWith('-service') || false;
  const isDocumentChecklistPhase = !isUploadsPhase && !isStoerungenPhase && !isMaintenancePhase && !isAbschlussberichtPhase && !isProjektdokumentationPhase && !isProjektvorbereitungPhase;
  const dealId = phaseId?.split('-')[0] || '';

  // Code-Review-Finding (Re-Review, zu #1199): vaillantEnqueueFailure ist reiner React-State
  // und würde einen Reload/Remount sonst nicht überleben - der ambivalente Zustand (Vaillant
  // hat den vorherigen Versuch möglicherweise trotzdem verarbeitet) wäre dann unbemerkt
  // verschwunden und das Dokument sofort wieder auswählbar. Stellt den Marker beim Mount aus
  // dem separaten localStorage-Backup wieder her (siehe writeVaillantEnqueueFailure oben).
  useEffect(() => {
    if (!dealId) return;
    const restored = readVaillantEnqueueFailure(dealId);
    if (restored) {
      setVaillantEnqueueFailure(restored);
    }
    const restoredObsoleteId = readVaillantObsoleteItemId(dealId);
    if (restoredObsoleteId) {
      setVaillantObsoleteItemId(restoredObsoleteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const progress = phaseId ? getPhaseProgress(phaseId) : { completed: 0, required: 0 };
  const isPhaseCompleteByProgress = progress.required > 0 && progress.completed >= progress.required;
  const isPhaseReadOnly = phase?.status === 'completed' && isPhaseCompleteByProgress;

  // Bestätigungspflichtiges Vaillant-Registrierungs-Item für diesen Deal, falls vorhanden
  // (siehe requiresManualConfirmation in interfaces/queue.ts): wird NICHT automatisch
  // verarbeitet, sondern muss der Monteur hier explizit bestätigen, nachdem er geprüft hat,
  // ob die Registrierung nicht doch schon durchgelaufen ist (#1199, Code-Review-Finding zur
  // fehlenden Idempotenz der Vaillant-Registrierung bei einem Timeout).
  const pendingVaillantRegistration = useMemo(
    () => queueItems.find(
      (item) => item.type === 'vaillant_serial_register' && item.data?.dealId === dealId
    ),
    [queueItems, dealId]
  );

  const handleConfirmVaillantRegistration = async () => {
    if (!pendingVaillantRegistration || confirmingVaillantRegistration) return;
    // Code-Review-Finding (P1): derselbe Guard wie in handleSeriennummerRegistrierenSubmit -
    // verhindert, dass die Banner-Bestätigung parallel zu einem (eigentlich blockierten, aber
    // z.B. aus einem schon offenen Modal heraus noch möglichen) Live-Submit läuft.
    if (vaillantRegistrationInFlightRef.current) {
      showToast('info', 'Eine Registrierung läuft bereits — bitte kurz warten.', 3000);
      return;
    }
    vaillantRegistrationInFlightRef.current = true;
    setConfirmingVaillantRegistration(true);
    try {
      // Code-Review-Finding (Re-Review): dieses Item ist als bereits erfolgreich
      // registriert bekannt (eine spätere Live-Registrierung ist durchgelaufen, nur die
      // Queue-Bereinigung selbst schlug fehl) - eine "Bestätigung" darf HIER NICHT
      // registerVaillantSerialNumber erneut aufrufen, das wäre eine dritte Registrierung.
      // Stattdessen sicherer No-Op: nur die (evtl. inzwischen wieder mögliche) Löschung
      // erneut versuchen, ohne Vaillant zu kontaktieren.
      if (pendingVaillantRegistration.id === vaillantObsoleteItemId) {
        try {
          await removeFromQueue(pendingVaillantRegistration.id);
          setVaillantObsoleteItemId(null);
          clearVaillantObsoleteItemId(dealId);
        } catch (removeErr: unknown) {
          console.error('[SeriennummerRegistrieren] Veraltetes Item weiterhin nicht entfernbar:', removeErr);
        }
        showToast('info', 'Diese Registrierung wurde bereits erfolgreich durchgeführt — kein erneuter Versuch nötig.', 4000);
        return;
      }
      const result = await confirmPendingItem(pendingVaillantRegistration.id);
      if (!result) {
        showToast('error', 'Bestätigung fehlgeschlagen — bitte erneut versuchen, sobald online.', 4000);
        return;
      }
      if (result.permanentlyFailed.includes(pendingVaillantRegistration.id)) {
        showToast('error', 'Seriennummer konnte nicht registriert werden', 4000);
      } else if (result.successful.includes(pendingVaillantRegistration.id)) {
        showToast('success', 'Seriennummer erfolgreich registriert ✅', 3000);
      } else {
        showToast('info', 'Noch nicht abgeschlossen — bitte später erneut versuchen.', 4000);
      }
    } finally {
      setConfirmingVaillantRegistration(false);
      vaillantRegistrationInFlightRef.current = false;
    }
  };

  // Auto-Abschluss: Nur auslösen wenn isPhaseCompleteByProgress false→true wechselt
  useEffect(() => {
    const wasComplete = prevProgressCompleteRef.current;
    prevProgressCompleteRef.current = isPhaseCompleteByProgress;

    if (
      !wasComplete &&
      isPhaseCompleteByProgress &&
      phase?.status !== 'completed' &&
      !completing &&
      isDocumentChecklistPhase &&
      !loading
    ) {
      completePhase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhaseCompleteByProgress]);

  // Countdown: jede Sekunde dekrementieren → Navigation bei 0
  useEffect(() => {
    if (completionCountdown === null) return;
    if (completionCountdown <= 0) {
      const target = completionNavTarget;
      setCompletionCountdown(null);
      setCompletionNavTarget(null);
      if (target) navigate(target, { replace: true });
      return;
    }
    const timer = setTimeout(() => {
      setCompletionCountdown(c => (c !== null ? c - 1 : null));
    }, 1000);
    return () => clearTimeout(timer);
  }, [completionCountdown, completionNavTarget, navigate]);

  const handleCancelCompletion = () => {
    setCompletionCountdown(null);
    setCompletionNavTarget(null);
  };

  const retryLoad = () => {
    setError(null);
    setReloadKey((k) => k + 1);
  };

  const navigateToProjectsOverview = () => {
    navigate('/projects', { replace: true });
  };

  const navigateToPhaseOverview = () => {
    navigate(projectId ? `/projects/${projectId}` : '/projects', { replace: true });
  };

  // HubSpot Deal-ID und Phase-Typ-Flags werden oben (vor den useEffects) deklariert

  // Bereits bekannte Seriennummer der Außeneinheit (lokaler Cache oder HubSpot-Deal,
  // gemeinsame Fallback-Kette mit FormularModal.tsx, siehe getKnownAusseneinheitSerialNumberUtil)
  const getKnownAusseneinheitSerialNumber = () =>
    getKnownAusseneinheitSerialNumberUtil(dealId, activeDeal);

  // Dokument für Upload auswählen
  const handleDocumentSelect = (document: DocumentType) => {
    setSelectedDocument(document);

    if (document._id === 'seriennummer-ausseneinheit') {
      // (document as any).fileId kann bei diesem Dokument entweder die echte
      // Seriennummer (Text-Property) oder eine hochgeladene Foto-Datei-ID sein
      // (HubSpot numerisch oder Google-Drive alphanumerisch). Nur übernehmen,
      // wenn der Wert tatsächlich dem Vaillant-Seriennummernformat entspricht.
      const rawFileId = (document as any).fileId;
      const fileIdLooksLikeSerialNumber = !!rawFileId && validateVaillantSerialNumber(rawFileId).valid;
      const ausseneinheitSn = (fileIdLooksLikeSerialNumber ? rawFileId : undefined)
        || getKnownAusseneinheitSerialNumber();
      setSeriennummerAusseneinheitInitialValue(ausseneinheitSn);
      setShowSeriennummerModal(true);
    } else if (document._id === 'seriennummer-registrieren') {
      // Code-Review-Finding (P1): solange ein Bestätigungs-Item aussteht, darf kein neuer
      // Live-Registrierungsversuch parallel gestartet werden können - der würde nicht über
      // den Queue-`isProcessing`-Guard laufen und könnte gleichzeitig mit einer Bestätigung
      // über den Banner (confirmPendingItem) zwei nicht-idempotente Vaillant-Requests
      // auslösen. Stattdessen auf den Banner verweisen. Dasselbe gilt (Re-Review), wenn selbst
      // das Vormerken fehlgeschlagen ist (vaillantEnqueueFailure) - auch dann ist der
      // ursprüngliche Versuch weiterhin ambivalent.
      if (pendingVaillantRegistration || vaillantEnqueueFailure?.dealId === dealId) {
        showToast(
          'info',
          'Es gibt bereits eine unbestätigte Registrierung — bitte zuerst oben in der Dokumentenliste bestätigen.',
          4000,
        );
        return;
      }
      const sn = getKnownAusseneinheitSerialNumberUtil(dealId, activeDeal);
      if (!sn) {
        // Noch keine Seriennummer bekannt – direkt die Erfassung öffnen, statt das
        // leere Registrieren-Modal zu zeigen. Nach dem Speichern kehrt der Flow
        // automatisch hierher zurück (siehe handleSeriennummerSubmit).
        setSeriennummerAusseneinheitInitialValue(getKnownAusseneinheitSerialNumber());
        setReopenRegistrierenAfterAusseneinheit(true);
        setShowSeriennummerModal(true);
        return;
      }
      setSeriennummerRegistrierenInitialValue(sn);
      setShowSeriennummerRegistrierenModal(true);
    } else if (document._id === 'typenschild-altanlage-durchfuehrung') {
      setShowCaptureModal(true);
    } else if (isDurchfuehrungPhase) {
      setShowFileUploadModal(true);
    } else {
      setShowCaptureModal(true);
    }
  };

  // Bild nach Aufnahme verarbeiten (für HubSpot - NICHT für Arbeitsberichte)
  const handleImageCapture = async (imageData: string) => {
    if (!selectedDocument) return;
    
    try {
      // ✅ B-03: Optimistic Update SOFORT (bevor API-Call)
      if (phaseId) {
        updateDocumentStatus(phaseId, selectedDocument._id, 'completed');
      }

      // ✅ B-03: Success-Toast sofort anzeigen
      showToast('success', `${selectedDocument.name} hochgeladen ✅`, 2000);
      
      // Bild in Queue für Upload hinzufügen (asynchron im Hintergrund)
      // Queue-Service uploaded zu Google Drive (mit dealName) oder Fallback HubSpot.
      // Kein await: läuft im Hintergrund weiter, während das Modal schon schließt. Rollback
      // ist in zwei Fällen nötig (Code-Review-Finding zu #1199, siehe AbschlussberichtWizard
      // für dasselbe Muster): ein Reject (z.B. Speicherplatz voll — der äußere try/catch
      // dieser Funktion fängt das NICHT ab, da hier nicht awaited wird) UND ein resolved
      // Ergebnis mit `permanentlyFailed` (sofortige Online-Verarbeitung schlägt endgültig
      // fehl, z.B. abgelaufene Session oder vom Server abgelehnter Dateityp — die Datei ist
      // dann unwiderruflich weg, ohne dass addToQueue dafür rejected).
      const rollbackDocumentUpload = (message: string) => {
        if (phaseId && selectedDocument) {
          updateDocumentStatus(phaseId, selectedDocument._id, 'pending');
        }
        showToast('error', message, 4000);
      };
      addToQueue('document_upload', {
        imageData,
        documentId: selectedDocument._id,
        phaseId: phaseId || '',
        projectId: projectId || '',
        dealName: dealName || '',
        sharedDrive: getSharedDriveName(),
        gdriveFolderId,
      } satisfies DocumentUploadQueueData)
        .then((result) => {
          if (result && result.permanentlyFailed.length > 0) {
            rollbackDocumentUpload('Upload fehlgeschlagen ⚠️');
          }
        })
        .catch((err: unknown) => {
          if (err instanceof QueueItemLockTimeoutError) {
            console.warn('[DocumentUpload] Speichern hängt noch (Lock-Timeout) - kein Rollback, Item ggf. später sichtbar.');
            return;
          }
          rollbackDocumentUpload(err instanceof Error ? err.message : 'Upload fehlgeschlagen ⚠️');
        });

      // Modal schließen
      setShowCaptureModal(false);
      setSelectedDocument(null);
      
    } catch (err: any) {
      // ✅ B-03: Rollback bei Fehler (zurück auf 'pending')
      if (phaseId && selectedDocument) {
        updateDocumentStatus(phaseId, selectedDocument._id, 'pending');
      }
      
      // Error-Toast
      showToast('error', 'Upload fehlgeschlagen ⚠️', 4000);
      setError(err.message || 'Fehler bei der Bildverarbeitung');
    }
  };

  // Phase abschließen
  const completePhase = async () => {
    if (!phase || !phaseId) return;
    
    // Finale Progress-Prüfung (Guard gegen Race Conditions)
    const finalProgress = getPhaseProgress(phaseId);
    if (finalProgress.completed < finalProgress.required) {
      setError(`Noch nicht alle Pflichtdokumente hochgeladen (${finalProgress.completed}/${finalProgress.required})`);
      return;
    }
    
    try {
      setCompleting(true);
      
      const completedAt = new Date().toISOString();
      
      // ✅ B-02: Store-Update für Phase-Status (Persistenz!)
      updatePhaseStatus(phaseId, 'completed', completedAt);
      
      // UI aktualisieren
      setPhase({ ...phase, status: 'completed', completedAt, completedByEmail: undefined });
      
      // Clear errors
      setError(null);

      // Nächste Phase oder Projektliste bestimmen
      const currentIndex = allPhases.findIndex(p => p._id === phaseId);
      const nextPhase = allPhases[currentIndex + 1];
      const isLastPhase = !nextPhase;
      const navTarget = isLastPhase
        ? '/projects'
        : `/projects/${projectId}/phase/${nextPhase._id}`;

      if (isLastPhase) {
        showToast('success', 'Projekt vollständig dokumentiert! 🏆', 5000);
      }

      // Countdown-Toast starten (3s → dann Navigation)
      setCompletionNavTarget(navTarget);
      setCompletionCountdown(3);
      
    } catch (err: any) {
      setError(err.message || 'Fehler beim Abschließen der Phase');
      // ✅ B-02: Error-Toast
      showToast('error', err.message || 'Fehler beim Abschließen der Phase', 4000);
      // ✅ BUGFIX: Also scroll to top on error so user sees error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setCompleting(false);
    }
  };

  // Progress, isPhaseCompleteByProgress, isPhaseReadOnly werden oben (vor den useEffects) deklariert

  // Fehlerbehandlung für DocumentChecklist
  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  // Multi-Upload: Dateien verarbeiten (Legacy - für HubSpot Dokumente)
  const handleFileUpload = async (files: UploadFile[], documentOverride?: DocumentType) => {
    const targetDocument = documentOverride || selectedDocument;
    if (!targetDocument || !phaseId) return;
    
    try {
      // Status sofort als completed markieren (optimistic update)
      updateDocumentStatus(phaseId, targetDocument._id, 'completed');

      // Ein Queue-Item mit mehreren Dateien verhindert parallele Patch-Rennen.
      // Kein await, siehe handleImageCapture weiter oben — Rollback bei Reject UND bei
      // resolved `permanentlyFailed` nötig (Code-Review-Finding zu #1199, siehe dort).
      const rollbackFileUpload = (message: string) => {
        updateDocumentStatus(phaseId, targetDocument._id, 'pending');
        showToast('error', message, 3000);
      };
      addToQueue('document_upload', {
        files: files.map((file) => ({
          id: file.id,
          name: file.name,
          type: file.type,
          size: file.size,
          dataUrl: file.dataUrl,
        })),
        documentId: targetDocument._id,
        phaseId,
        projectId,
        dealName: dealName || '',
        existingFiles: stripUploadedDocumentFilesRuntimeData(getDocumentFiles(phaseId, targetDocument._id)),
        sharedDrive: getSharedDriveName(),
        gdriveFolderId,
      } satisfies DocumentUploadQueueData)
        .then((result) => {
          if (result && result.permanentlyFailed.length > 0) {
            rollbackFileUpload('Upload fehlgeschlagen');
          }
        })
        .catch((err: unknown) => {
          if (err instanceof QueueItemLockTimeoutError) {
            console.warn('[FileUpload] Speichern hängt noch (Lock-Timeout) - kein Rollback, Item ggf. später sichtbar.');
            return;
          }
          console.error('Upload-Fehler:', err);
          rollbackFileUpload(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
        });

      showToast('success', `${files.length} Datei(en) hochgeladen ✅`, 2000);
    } catch (err) {
      console.error('Upload-Fehler:', err);
      showToast('error', 'Upload fehlgeschlagen', 3000);
    }
    
    setShowFileUploadModal(false);
    setSelectedDocument(null);
  };

  const handleSeriennummerSubmit = async (serialNumber: string, imageData: string | null) => {
    setShowSeriennummerModal(false);
    setSelectedDocument(null);

    const durchfuehrungPhaseId = `${dealId}-durchfuehrung`;
    const shouldReopenRegistrieren = reopenRegistrierenAfterAusseneinheit;
    setReopenRegistrierenAfterAusseneinheit(false);

    // Optimistic-First (wie handleImageCapture): lokal sofort als erledigt markieren,
    // unabhängig vom Netzwerk. Seriennummer-Außeneinheit ist Pflichtfeld in der
    // Baustellendoku — ein blockierender Live-Call würde offline erfasste Seriennummern
    // sonst dauerhaft als "offen" stehen lassen, obwohl der Monteur alles Nötige getan hat.
    saveDocumentData(durchfuehrungPhaseId, 'seriennummer-ausseneinheit-text', serialNumber);
    updateDocumentStatus(durchfuehrungPhaseId, 'seriennummer-ausseneinheit', 'completed');

    const cachedDealAfterAusseneinheit = useDealsStore.getState().deals.find(d => d.id === dealId);
    if (cachedDealAfterAusseneinheit) {
      upsertDeal({ ...cachedDealAfterAusseneinheit, seriennummer_der_warmepumpe: serialNumber });
    }
    setActiveDeal((prev) => (prev ? { ...prev, seriennummer_der_warmepumpe: serialNumber } : prev));

    if (imageData) {
      // Rollback bei Reject UND bei resolved `permanentlyFailed` nötig, siehe
      // handleImageCapture weiter oben (Code-Review-Finding zu #1199).
      const rollbackAusseneinheitUpload = (message: string) => {
        updateDocumentStatus(durchfuehrungPhaseId, 'seriennummer-ausseneinheit', 'pending');
        showToast('error', message, 4000);
      };
      addToQueue('document_upload', {
        imageData,
        documentId: 'seriennummer-ausseneinheit',
        phaseId: durchfuehrungPhaseId,
        projectId: projectId || '',
        dealName: dealName || '',
        sharedDrive: getSharedDriveName(),
        gdriveFolderId,
        serialNumber,
      } satisfies DocumentUploadQueueData)
        .then((result) => {
          if (result && result.permanentlyFailed.length > 0) {
            rollbackAusseneinheitUpload('Foto konnte nicht gespeichert werden.');
          }
        })
        .catch((err: unknown) => {
          if (err instanceof QueueItemLockTimeoutError) {
            console.warn('[Seriennummer] Foto-Upload hängt noch (Lock-Timeout) - kein Rollback, Item ggf. später sichtbar.');
            return;
          }
          console.error('[Seriennummer] Foto-Upload konnte nicht in die Queue eingetragen werden:', err);
          rollbackAusseneinheitUpload(err instanceof Error ? err.message : 'Foto konnte nicht gespeichert werden.');
        });
    }

    // Kam der Aufruf über "Hinzufügen" aus dem Registrieren-Modal, dorthin zurückkehren,
    // damit direkt weiter registriert werden kann, ohne erneut navigieren zu müssen.
    if (shouldReopenRegistrieren) {
      setSeriennummerRegistrierenInitialValue(serialNumber);
      setShowSeriennummerRegistrierenModal(true);
    }

    const propertyOperationId = crypto.randomUUID();
    try {
      console.log('[Seriennummer] dealId:', dealId, '| serialNumber:', serialNumber);
      await updateDealTextProperty(dealId, 'seriennummer_der_warmepumpe', serialNumber, propertyOperationId);
      showToast('success', 'Seriennummer gespeichert ✅', 2000);
    } catch (err: any) {
      console.error('[Seriennummer] Live-Update fehlgeschlagen, wird in die Queue gelegt:', err?.response?.data ?? err?.message ?? err);
      // Netzwerkfehler (z. B. offline) blockiert nicht die Fertigstellung — HubSpot-Schreiben
      // wird nachgeholt, sobald die Queue wieder online synchronisiert.
      addToQueue('hubspot_text_property_update', {
        dealId,
        property: 'seriennummer_der_warmepumpe',
        idempotencyKey: propertyOperationId,
        value: serialNumber,
      } satisfies HubspotTextPropertyQueueData)
        .then((result) => {
          // Code-Review-Finding (Re-Review): ein sofortiger endgültiger Fehlschlag lässt
          // addToQueue normal auflösen (permanentlyFailed statt successful) - der optimistisch
          // gezeigte "Offline gespeichert"-Toast unten muss dann nachträglich korrigiert werden.
          if (result && result.permanentlyFailed.length > 0) {
            showToast('error', 'Seriennummer konnte nicht gespeichert werden.', 4000);
          }
        })
        .catch((queueErr: unknown) => {
          if (queueErr instanceof QueueItemLockTimeoutError) {
            console.warn('[Seriennummer] Retry-Eintrag hängt noch (Lock-Timeout) - kein Fehler-Toast, Ergebnis bleibt unbestimmt.');
            return;
          }
          console.error('[Seriennummer] Retry konnte nicht in die Queue eingetragen werden:', queueErr);
          showToast('error', queueErr instanceof Error ? queueErr.message : 'Seriennummer konnte nicht gespeichert werden.', 4000);
        });
      showToast('info', 'Offline gespeichert – wird synchronisiert, sobald wieder online', 3000);
    }
  };

  // "Hinzufügen"-Button im Registrieren-Modal: Außeneinheit-Modal öffnen, danach hierher zurückkehren.
  const handleOpenSeriennummerAusseneinheitFromRegistrieren = (currentValue: string) => {
    setShowSeriennummerRegistrierenModal(false);
    setSeriennummerAusseneinheitInitialValue(currentValue || getKnownAusseneinheitSerialNumber());
    setReopenRegistrierenAfterAusseneinheit(true);
    setShowSeriennummerModal(true);
  };

  // Gemeinsamer Fallback für beide "Netzwerk-/serverseitig fehlgeschlagen"-Stellen in
  // handleSeriennummerRegistrierenSubmit (Exception beim Live-Call UND success:false mit
  // 5xx-statusCode) - einmal korrekt implementiert statt dupliziert (Code-Review-Finding
  // Re-Review: Reject UND resolved permanentlyFailed müssen ausgewertet werden).
  // Code-Review-Finding (P1, Re-Review): MUSS awaited werden, statt fire-and-forget zu laufen.
  // Der Aufrufer (performSeriennummerRegistrierenSubmit) hält den gemeinsamen In-Flight-Guard
  // nur, solange diese Funktion läuft - ohne await würde der Guard sofort zurückgesetzt,
  // bevor addToQueue das Pending-Item persistiert und im UI sichtbar gemacht hat (items via
  // setItems). In diesem Fenster bliebe das Dokument auswählbar und ein neuer Live-Submit
  // könnte parallel zum eigentlich schon (möglicherweise erfolgreichen) ersten Versuch einen
  // zweiten, nicht-idempotenten Vaillant-Request auslösen.
  const queueVaillantSerialRegisterFallback = async (
    fallbackData: VaillantSerialRegisterQueueData,
  ): Promise<void> => {
    // vaillant_serial_register ist ein bestätigungspflichtiger Queue-Typ (siehe
    // requiresManualConfirmation in interfaces/queue.ts) - addToQueue verarbeitet es NIE
    // automatisch, weder sofort noch bei einem späteren Reconnect/"Sync jetzt". Das Item
    // bleibt bewusst liegen, bis der Monteur es über den Banner oben in der Dokumentenliste
    // (handleConfirmVaillantRegistration) explizit bestätigt — ein automatischer Retry könnte
    // sonst eine bereits erfolgreich durchgelaufene, nicht-idempotente Vaillant-Registrierung
    // ein zweites Mal auslösen (Code-Review-Finding zu #1199).
    try {
      await addToQueue('vaillant_serial_register', {
        ...fallbackData,
        // Ersetzt ein evtl. schon offenes Item für denselben Deal statt einen zweiten,
        // konkurrierenden Eintrag anzulegen (siehe VaillantSerialRegisterQueueData).
        queueKey: `vaillant_serial_register:${fallbackData.dealId}`,
      } satisfies VaillantSerialRegisterQueueData);
    } catch (queueErr: unknown) {
      // Code-Review-Finding (Re-Review): das Vormerken selbst ist fehlgeschlagen - es gibt
      // jetzt GAR KEIN Queue-Item, das den ambivalenten Zustand des ursprünglichen Versuchs
      // repräsentiert. Ohne dieses In-Memory-Backup würde das Dokument sofort wieder
      // auswählbar und der Guard freigegeben, obwohl der Live-Request weiterhin ambivalent
      // ist. fallbackData wird aufgehoben, damit der Monteur das Vormerken über den Banner
      // gezielt erneut versuchen kann (handleRetryVaillantEnqueue), statt live erneut zu
      // registrieren.
      setVaillantEnqueueFailure(fallbackData);
      writeVaillantEnqueueFailure(fallbackData);
      // Bei einem Lock-Timeout steht NICHT fest, dass das Vormerken tatsächlich gescheitert ist
      // (siehe addToQueue-Doku in QueueContext.tsx) - eine Formulierung, die einen definitiven
      // Fehlschlag suggeriert, würde den Monteur fälschlich zu einem Retry verleiten, obwohl der
      // erste Versuch im Hintergrund noch abschließen könnte.
      if (queueErr instanceof QueueItemLockTimeoutError) {
        console.warn('[SeriennummerRegistrieren] Vormerken hängt noch (Lock-Timeout):', queueErr);
        showToast(
          'error',
          'Registrierung konnte nicht rechtzeitig bestätigt werden — bitte oben in der Dokumentenliste prüfen, bevor eine neue Registrierung gestartet wird.',
          6000,
        );
        return;
      }
      console.error('[SeriennummerRegistrieren] Konnte nicht in die Queue eingetragen werden:', queueErr);
      showToast(
        'error',
        'Seriennummer konnte nicht vorgemerkt werden — bitte oben in der Dokumentenliste erneut versuchen, bevor eine neue Registrierung gestartet wird.',
        6000,
      );
      return;
    }
    // Ein evtl. vorheriger Enqueue-Fehlschlag für denselben Deal ist jetzt aufgelöst (z.B.
    // durch einen erfolgreichen Retry über handleRetryVaillantEnqueue).
    setVaillantEnqueueFailure(null);
    clearVaillantEnqueueFailure(fallbackData.dealId);
    showToast(
      'info',
      'Registrierung konnte nicht bestätigt werden — bitte oben in der Dokumentenliste prüfen und bestätigen.',
      5000,
    );
  };

  const handleRetryVaillantEnqueue = async () => {
    if (!vaillantEnqueueFailure || retryingVaillantEnqueue) return;
    if (vaillantRegistrationInFlightRef.current) {
      showToast('info', 'Eine Registrierung läuft bereits — bitte kurz warten.', 3000);
      return;
    }
    vaillantRegistrationInFlightRef.current = true;
    setRetryingVaillantEnqueue(true);
    try {
      await queueVaillantSerialRegisterFallback(vaillantEnqueueFailure);
    } finally {
      setRetryingVaillantEnqueue(false);
      vaillantRegistrationInFlightRef.current = false;
    }
  };

  // Code-Review-Finding (P1): dünner Wrapper statt den Guard an jedem der mehreren
  // Return-Punkte in performSeriennummerRegistrierenSubmit einzeln zurückzusetzen (fehler-
  // anfällig - ein nicht abgefangener Fehler dort würde den Guard sonst dauerhaft blockieren).
  // try/finally garantiert das Zurücksetzen unabhängig vom Ausgang.
  const handleSeriennummerRegistrierenSubmit = async (serialNumber: string) => {
    if (vaillantRegistrationInFlightRef.current) {
      showToast('info', 'Eine Registrierung läuft bereits — bitte kurz warten.', 3000);
      return;
    }
    vaillantRegistrationInFlightRef.current = true;
    try {
      await performSeriennummerRegistrierenSubmit(serialNumber);
    } finally {
      vaillantRegistrationInFlightRef.current = false;
    }
  };

  const performSeriennummerRegistrierenSubmit = async (serialNumber: string) => {
    setShowSeriennummerRegistrierenModal(false);
    setSelectedDocument(null);
    const inbetriebnahmePhaseId = `${dealId}-inbetriebnahme`;

    // Optimistic-First (wie handleSeriennummerSubmit): lokal sofort übernehmen, unabhängig
    // vom Netzwerk. Ein Live-Fehler beim Speichern der Seriennummer soll die anschließende
    // Vaillant-Registrierung nicht blockieren.
    saveDocumentData(inbetriebnahmePhaseId, 'seriennummer-registrieren-text', serialNumber);
    const cachedDealBeforeRegistrieren = useDealsStore.getState().deals.find(d => d.id === dealId);
    if (cachedDealBeforeRegistrieren) {
      upsertDeal({ ...cachedDealBeforeRegistrieren, seriennummer_der_warmepumpe: serialNumber });
    }
    setActiveDeal((prev) => (prev ? { ...prev, seriennummer_der_warmepumpe: serialNumber } : prev));

    const propertyOperationId = crypto.randomUUID();
    try {
      await updateDealTextProperty(dealId, 'seriennummer_der_warmepumpe', serialNumber, propertyOperationId);
    } catch (err: unknown) {
      console.error('[SeriennummerRegistrieren] Seriennummer-Update fehlgeschlagen, wird in die Queue gelegt:', describeApiError(err));
      addToQueue('hubspot_text_property_update', {
        dealId,
        property: 'seriennummer_der_warmepumpe',
        idempotencyKey: propertyOperationId,
        value: serialNumber,
      } satisfies HubspotTextPropertyQueueData)
        .then((result) => {
          // Code-Review-Finding (Re-Review): siehe handleSeriennummerSubmit weiter oben -
          // ein sofortiger endgültiger Fehlschlag löst normal auf statt zu rejecten.
          if (result && result.permanentlyFailed.length > 0) {
            showToast('error', 'Seriennummer konnte nicht gespeichert werden.', 4000);
          }
        })
        .catch((queueErr: unknown) => {
          if (queueErr instanceof QueueItemLockTimeoutError) {
            console.warn('[SeriennummerRegistrieren] Retry-Eintrag hängt noch (Lock-Timeout) - kein Fehler-Toast, Ergebnis bleibt unbestimmt.');
            return;
          }
          // Ein Reject hier bedeutet, dass die Seriennummer WEDER live noch als Retry
          // gespeichert werden konnte - bisher wurde das nur geloggt, ohne dass der Monteur
          // je davon erfuhr.
          console.error('[SeriennummerRegistrieren] Retry konnte nicht in die Queue eingetragen werden:', queueErr);
          showToast('error', queueErr instanceof Error ? queueErr.message : 'Seriennummer konnte nicht gespeichert werden.', 4000);
        });
    }

    // Netzwerkfehler/Timeout/5xx bei der Registrierung selbst: hier wird bewusst trotzdem
    // ein kompletter Retry (inkl. erneutem Registrierungsversuch) in Kauf genommen, OBWOHL
    // ein Timeout/Verbindungsabbruch NICHT beweist, dass Vaillant die Anfrage nicht doch
    // verarbeitet hat (Code-Review-Finding, korrigiert eine frühere, falsche Formulierung
    // hier: "nachweislich nicht verarbeitet" trifft nicht zu). Seit #1202 ist das serverseitig
    // per Idempotency-Key abgesichert (siehe unten) — ein Retry mit demselben Key kontaktiert
    // Vaillant bei einer bereits erfolgreichen vorherigen Registrierung NICHT erneut. Vorher
    // gab es hierfür (im Gegensatz zur Außeneinheit-Seriennummer in handleSeriennummerSubmit)
    // gar kein Queue-Fallback, was den in #1199 gemeldeten Fehler "Seriennummer konnte nicht
    // registriert werden" ohne jede Möglichkeit für einen späteren Retry erzeugte.
    // Ein Key pro Registrierungsversuch, unverändert für alle Retries dieses Versuchs
    // (Live-Call hier UND jeder spätere Queue-Retry über queueVaillantSerialRegisterFallback/
    // processVaillantSerialRegister) — siehe registerVaillantSerialNumber-Doku.
    const idempotencyKey = crypto.randomUUID();
    let data: Awaited<ReturnType<typeof registerVaillantSerialNumber>>;
    try {
      data = await registerVaillantSerialNumber(serialNumber, dealContact?.email ?? undefined, undefined, idempotencyKey);
    } catch (err: unknown) {
      console.error('[SeriennummerRegistrieren] Registrierung fehlgeschlagen, wird in die Queue gelegt:', describeApiError(err));
      await queueVaillantSerialRegisterFallback({
        dealId,
        serialNumber,
        contactEmail: dealContact?.email ?? undefined,
        inbetriebnahmePhaseId,
        idempotencyKey,
      });
      return;
    }

    if (!data.success) {
      const detail =
        typeof data.response === 'object' && data.response !== null && 'detail' in data.response
          ? String((data.response as { detail?: unknown }).detail ?? 'Ungültige Seriennummer')
          : 'Ungültige Seriennummer';
      // `success: false` kommt meist als HTTP 200 zurück (statusCode spiegelt dann nur die
      // äußere Antwort, nicht die Ablehnung selbst), kann laut Response-Vertrag über
      // `statusCode` aber auch eine transiente serverseitige Nichtverfügbarkeit von Vaillant
      // selbst (5xx) oder ein Rate-Limit (429) transportieren — beides retry-fähig. Alles
      // andere (200, 4xx, unbekannt) ist eine permanente inhaltliche Ablehnung (z.B. ungültige
      // Seriennummer). Dieselbe Klassifizierung wie classifyVaillantRejectionStatus in
      // queueService.ts (Code-Review-Finding, Re-Review: ein statusCode von 200 hätte hier
      // fälschlich als "keine 5xx" durchgehen können, ist aber nie transient gemeint).
      if (typeof data.statusCode === 'number' && (data.statusCode >= 500 || data.statusCode === 429)) {
        console.error(`[SeriennummerRegistrieren] Registrierung serverseitig fehlgeschlagen (Status ${data.statusCode}), wird in die Queue gelegt:`, detail);
        await queueVaillantSerialRegisterFallback({
          dealId,
          serialNumber,
          contactEmail: dealContact?.email ?? undefined,
          inbetriebnahmePhaseId,
          idempotencyKey,
        });
        return;
      }
      showToast('error', `Registrierung fehlgeschlagen: ${detail}`, 5000);
      return;
    }

    // Ab hier ist die Registrierung bei Vaillant unwiderruflich passiert (der Kunde bekommt
    // eine Bestätigungsmail) — bei einem Fehler in den Folgeschritten darf NICHT mehr
    // 'vaillant_serial_register' erneut gequeued werden, das würde die Registrierung
    // doppelt auslösen. Nur noch der reine HubSpot-Consent-Schreibvorgang wird nachgeholt.

    // Falls für diesen Deal noch ein unbestätigtes vaillant_serial_register-Item aus einem
    // früheren, ambivalenten Versuch in der Queue liegt (Banner oben), ist es jetzt obsolet -
    // diese frische Live-Registrierung ist gerade erfolgreich durchgelaufen. Ohne dieses
    // Aufräumen könnte der Monteur später "Jetzt erneut versuchen" auf dem veralteten Banner
    // klicken und eine dritte Registrierung auslösen.
    // Code-Review-Finding (Re-Review): MUSS awaited werden - removeQueueItem/removeFromQueue
    // können jetzt bei einem Persistenzfehler werfen (siehe queueService.ts), und ein
    // fire-and-forget-Aufruf hier würde einen Fehlschlag nicht bemerken. Da removeFromQueue
    // den UI-State nur bei tatsächlichem Erfolg bereinigt (kein optimistisches setItems mehr
    // bei einem Reject), bliebe das veraltete Item ohne diesen await zwar korrekt sichtbar,
    // aber der Monteur würde nicht aktiv gewarnt, dass hier manuell nachgesehen werden muss.
    if (pendingVaillantRegistration) {
      try {
        await removeFromQueue(pendingVaillantRegistration.id);
      } catch (removeErr: unknown) {
        // Code-Review-Finding (Re-Review): ein Fehlschlag hier darf das Item NICHT weiterhin
        // ausführbar lassen - ohne diese Markierung bliebe der "Jetzt erneut versuchen"-Banner
        // bedienbar und confirmPendingItem würde registerVaillantSerialNumber ein drittes Mal
        // aufrufen, obwohl die Registrierung gerade eben schon erfolgreich war. Die Markierung
        // überlebt auch einen Reload (localStorage), da das Item selbst ja weiterhin (auch
        // nach einem Reload) in der durablen Queue liegen bleibt.
        console.error('[SeriennummerRegistrieren] Veraltetes Bestätigungs-Item konnte nicht entfernt werden:', removeErr);
        setVaillantObsoleteItemId(pendingVaillantRegistration.id);
        writeVaillantObsoleteItemId(dealId, pendingVaillantRegistration.id);
        showToast(
          'error',
          'Ein veralteter Registrierungs-Hinweis konnte nicht entfernt werden — er wurde als bereits erledigt markiert und kann gefahrlos ignoriert werden.',
          6000,
        );
      }
    }
    // Ein evtl. offener Enqueue-Fehlschlag für diesen Deal ist ebenfalls obsolet - die
    // Live-Registrierung ist gerade erfolgreich durchgelaufen.
    if (vaillantEnqueueFailure?.dealId === dealId) {
      setVaillantEnqueueFailure(null);
      clearVaillantEnqueueFailure(dealId);
    }

    const consentTimestamp = new Date().toISOString();
    const consentOperationId = crypto.randomUUID();
    try {
      await updateDealTextProperty(dealId, 'vaillant_api_consent_angefragt', consentTimestamp, consentOperationId);
    } catch (err: unknown) {
      console.error('[SeriennummerRegistrieren] Consent-Update fehlgeschlagen, wird in die Queue gelegt:', describeApiError(err));
      addToQueue('hubspot_text_property_update', {
        dealId,
        property: 'vaillant_api_consent_angefragt',
        idempotencyKey: consentOperationId,
        value: consentTimestamp,
      } satisfies HubspotTextPropertyQueueData)
        .then((result) => {
          // Code-Review-Finding (Re-Review): auch hier kann addToQueue bei einem sofortigen
          // endgültigen Fehlschlag normal auflösen statt zu rejecten.
          if (result && result.permanentlyFailed.length > 0) {
            showToast('error', 'Vaillant-Consent-Vermerk konnte nicht gespeichert werden.', 4000);
          }
        })
        .catch((queueErr: unknown) => {
          if (queueErr instanceof QueueItemLockTimeoutError) {
            console.warn('[SeriennummerRegistrieren] Consent-Retry-Eintrag hängt noch (Lock-Timeout) - kein Fehler-Toast, Ergebnis bleibt unbestimmt.');
            return;
          }
          // Registrierung selbst ist bereits durch (Erfolgs-Toast unten) - ein Reject hier
          // betrifft nur das Consent-Property in HubSpot, wurde vorher aber nur geloggt.
          console.error('[SeriennummerRegistrieren] Consent-Retry konnte nicht in die Queue eingetragen werden:', queueErr);
          showToast('error', 'Vaillant-Consent-Vermerk konnte nicht gespeichert werden.', 4000);
        });
    }

    // Code-Review-Finding (PR #431/#432): NICHT mehr 'seriennummer-registrieren-text'
    // wiederverwenden - dieser Schlüssel hält die bestätigte Seriennummer und wird von
    // getKnownAusseneinheitSerialNumber als höchste Priorität gelesen; ein Überschreiben mit
    // dem Consent-Zeitstempel führte dazu, dass sn_ag anschließend einen Zeitstempel statt
    // der Seriennummer vorausgefüllt bekam. Der Zeitstempel bekommt deshalb einen eigenen,
    // separaten Schlüssel (nur für die "Registrierung übermittelt"-Anzeige in
    // DocumentChecklist relevant).
    saveDocumentData(inbetriebnahmePhaseId, 'seriennummer-registrieren-consent-text', consentTimestamp);
    const cachedDeal = useDealsStore.getState().deals.find(d => d.id === dealId);
    if (cachedDeal) upsertDeal({ ...cachedDeal, vaillant_api_consent_angefragt: consentTimestamp });

    updateDocumentStatus(inbetriebnahmePhaseId, 'seriennummer-registrieren', 'completed');
    showToast('success', 'Seriennummer erfolgreich registriert ✅', 3000);
  };

  // UI-Elemente
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-x-hidden">
      {/* Header */}
      <AppHeader
        title={loading ? 'Lade Phase...' : phase?.name || 'Phase'}
        meta={phase?.project ? <span>{phase.project.name}</span> : undefined}
        backAction={{ onClick: navigateToPhaseOverview, ariaLabel: 'Zurück zur Phasen-Übersicht' }}
        actions={phase ? (
          phase.status === 'completed' ? (
            <span className="rounded-full bg-green-100 px-3 py-1.5 text-xs font-medium text-green-800">
              Abgeschlossen
            </span>
          ) : (
            <span className="rounded-full bg-blue-100 px-3 py-1.5 text-xs font-medium text-blue-800">
              In Bearbeitung
            </span>
          )
        ) : null}
        menuContent={(
          <>
            <button
              onClick={navigateToProjectsOverview}
              className="mt-1 flex w-full items-center rounded-xl px-3 py-3 text-left text-sm text-slate-700 transition hover:bg-slate-50"
            >
              <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Zur Projekt-Übersicht
            </button>
            <button
              onClick={logout}
              className="flex w-full items-center rounded-xl px-3 py-3 text-left text-sm text-red-600 transition hover:bg-red-50"
            >
              <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Abmelden
            </button>
          </>
        )}
        containerClassName="mx-auto max-w-[430px] px-4 py-4"
        useDesktopLogoOffset={false}
      />

      {/* Progress-Bar */}
      {progress.required > 0 && (
        <div className={`p-2 ${isPhaseCompleteByProgress ? 'bg-green-50 dark:bg-green-900/30' : 'bg-gray-50 dark:bg-gray-700'}`}>
          <div className="mx-auto flex max-w-[430px] items-center justify-between px-4">
            <span className={`text-sm font-medium flex items-center gap-1.5 ${isPhaseCompleteByProgress ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {isPhaseCompleteByProgress && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
              )}
              Pflichtdokumente: {progress.completed}/{progress.required}
            </span>
            <div className="w-32 rounded-full bg-gray-200 h-2.5 dark:bg-gray-600">
              <div
                className={`h-2.5 rounded-full transition-all duration-300 ${isPhaseCompleteByProgress ? 'bg-green-500' : 'bg-primary-600'}`}
                style={{
                  width: `${(progress.completed / progress.required) * 100}%`
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Hauptinhalt – pb berücksichtigt PhaseFooterNav (5rem) + Safe-Area + 1rem Buffer.
          BottomNavigation ist auf Phase-Screens ausgeblendet (App.tsx), darum kein 4rem-Aufschlag. */}
      <main className="max-w-[430px] mx-auto px-4 py-6 pb-[calc(env(safe-area-inset-bottom)+6rem)]">
        {/* Readonly-Banner für abgeschlossene Phasen */}
        {isPhaseReadOnly && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 mr-3 mt-0.5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-green-800 mb-1">
                  Phase abgeschlossen
                </h3>
                <p className="text-sm text-green-700">
                  {isDocumentChecklistPhase
                    ? 'Diese Phase wurde erfolgreich abgeschlossen. Fotos und Dokumente können weiterhin bearbeitet werden.'
                    : 'Diese Phase wurde erfolgreich abgeschlossen. Uploads sind nicht mehr möglich.'}
                  {phase.completedAt && (
                    <span className="block mt-1 text-xs text-green-600">
                      Abgeschlossen am: {new Date(phase.completedAt).toLocaleString('de-DE')}
                    </span>
                  )}
                  {phase.completedByEmail?.trim() && (
                    <span className="block mt-1 text-xs text-green-600">
                      Abgeschlossen von: {phase.completedByEmail.trim()}
                    </span>
                  )}
                  {phase.approvedByEmail && (
                    <span className="block mt-1 text-xs text-green-600">
                      Freigegeben von: {phase.approvedByEmail}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Fehleranzeige */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>{error}</span>
              </div>
              <div className="flex gap-2">
                <button 
                  className="text-sm px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-800"
                  onClick={navigateToPhaseOverview}
                >
                  Zurück
                </button>
                <button 
                  className="text-sm px-3 py-1 rounded bg-primary-600 hover:bg-primary-700 text-white"
                  onClick={retryLoad}
                >
                  Erneut versuchen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading-State */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <>
            {/* Phasenbeschreibung */}
            {phase?.description && !isProjektdokumentationPhase && !isProjektvorbereitungPhase && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Beschreibung
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  {phase.description}
                </p>
              </div>
            )}

            {isInbetriebnahmePhase && <InbetriebnahmeStatus key={dealId} dealId={dealId} />}

            {/* Allgemeine Uploads Phase (Issue #62) */}
            {isUploadsPhase ? (
              <AllgemeineUploads dealId={dealId} dealName={dealName} />
            ) : isStoerungenPhase ? (
              <StoerungenFormular dealName={dealName} hubspotId={dealId} />
            ) : isMaintenancePhase ? (
              <MaintenanceProtocols
                projectId={projectId || dealId}
                dealName={dealName}
                projectDetails={maintenanceProjectDetails}
              />
            ) : isProjektvorbereitungPhase ? (
              <ProjektvorbereitungBriefing
                projectId={projectId || dealId}
                phaseId={phaseId || ''}
                disabled={isPhaseReadOnly}
              />
            ) : isProjektdokumentationPhase ? (
              <ProjektdokumentationWizard
                projectId={projectId || dealId}
                phaseId={phaseId || ''}
                disabled={isPhaseReadOnly}
              />
            ) : isAbschlussberichtPhase ? (
              <AbschlussberichtWizard
                projectId={projectId || dealId}
                phaseId={phaseId || ''}
                disabled={isPhaseReadOnly}
              />
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white p-4 border-b dark:border-gray-700">
                  Dokumente
                </h2>

                {isInbetriebnahmePhase && pendingVaillantRegistration && (
                  <div className="m-4 p-4 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40">
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                      {pendingVaillantRegistration.id === vaillantObsoleteItemId
                        ? 'Registrierung bereits erfolgreich — Hinweis kann noch nicht entfernt werden'
                        : 'Vaillant-Registrierung nicht bestätigt'}
                    </p>
                    <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                      {pendingVaillantRegistration.id === vaillantObsoleteItemId ? (
                        <>
                          Die Seriennummer{' '}
                          <span className="font-mono">{pendingVaillantRegistration.data?.serialNumber}</span> wurde
                          bereits erfolgreich registriert. Dieser Hinweis konnte nur technisch noch nicht entfernt
                          werden — eine erneute Bestätigung kontaktiert Vaillant NICHT noch einmal.
                        </>
                      ) : (
                        <>
                          Der letzte Registrierungsversuch für Seriennummer{' '}
                          <span className="font-mono">{pendingVaillantRegistration.data?.serialNumber}</span> konnte
                          nicht bestätigt werden (Netzwerkfehler/Timeout). Die Registrierung bei Vaillant kann
                          trotzdem bereits erfolgt sein — bitte vor einem erneuten Versuch prüfen, ob der Kunde
                          bereits eine Bestätigungsmail von Vaillant erhalten hat, um eine doppelte Registrierung zu
                          vermeiden.
                        </>
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={handleConfirmVaillantRegistration}
                      disabled={confirmingVaillantRegistration}
                      className="mt-3 rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                    >
                      {confirmingVaillantRegistration
                        ? 'Wird versucht …'
                        : pendingVaillantRegistration.id === vaillantObsoleteItemId
                          ? 'Hinweis entfernen'
                          : 'Jetzt erneut versuchen'}
                    </button>
                  </div>
                )}

                {/* Code-Review-Finding (Re-Review): eigener Banner für den Fall, dass selbst
                    das Vormerken des Bestätigungs-Items fehlgeschlagen ist - es existiert dann
                    kein Queue-Item, das den ambivalenten Zustand tragen könnte. */}
                {isInbetriebnahmePhase && !pendingVaillantRegistration && vaillantEnqueueFailure?.dealId === dealId && (
                  <div className="m-4 p-4 rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/40">
                    <p className="text-sm font-semibold text-red-900 dark:text-red-200">
                      Vaillant-Registrierung konnte nicht einmal vorgemerkt werden
                    </p>
                    <p className="mt-1 text-sm text-red-800 dark:text-red-300">
                      Der letzte Registrierungsversuch für Seriennummer{' '}
                      <span className="font-mono">{vaillantEnqueueFailure.serialNumber}</span> konnte nicht bestätigt
                      werden UND nicht einmal lokal vorgemerkt werden (z.B. Speicherplatz auf dem Gerät voll). Die
                      Registrierung bei Vaillant kann trotzdem bereits erfolgt sein — bitte vor einem erneuten
                      Versuch prüfen, ob der Kunde bereits eine Bestätigungsmail erhalten hat, und zuerst hier das
                      Vormerken erneut versuchen (nicht direkt neu registrieren).
                    </p>
                    <button
                      type="button"
                      onClick={handleRetryVaillantEnqueue}
                      disabled={retryingVaillantEnqueue}
                      className="mt-3 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {retryingVaillantEnqueue ? 'Wird versucht …' : 'Vormerken erneut versuchen'}
                    </button>
                  </div>
                )}

	                <DocumentChecklist
	                  phaseId={phaseId || ''}
	                  projectId={projectId || ''}
	                  onSelectDocument={handleDocumentSelect}
	                  onDirectPhotoUpload={(document, files) => handleFileUpload(files, document)}
	                  onError={handleError}
	                  disabled={false}
                  dealContact={dealContact}
                  // Ticket #1126: "aussenfuehler" wird unten vor "seriennummer-ausseneinheit"
                  // gerendert, direkt bei der Baustellendokumentation.
                  excludeDocumentIds={isDurchfuehrungPhase ? ['aussenfuehler', 'seriennummer-ausseneinheit'] : undefined}
                />

                {isDurchfuehrungPhase && (
                  <>
                    {/* Ticket #1126: Außenfühler-Pflichtnachweis vor Seriennummer Außeneinheit,
                        direkt bei der Baustellendokumentation — gleiche Upload-/Statuslogik wie
                        jeder andere Checklisten-Eintrag, nur speziell platziert. */}
                    <DocumentChecklist
                      phaseId={phaseId || ''}
                      projectId={projectId || ''}
                      onSelectDocument={handleDocumentSelect}
                      onDirectPhotoUpload={(document, files) => handleFileUpload(files, document)}
                      onError={handleError}
                      disabled={false}
                      dealContact={dealContact}
                      onlyDocumentIds={['aussenfuehler']}
                    />
                    <DocumentChecklist
                      phaseId={phaseId || ''}
                      projectId={projectId || ''}
                      onSelectDocument={handleDocumentSelect}
                      onDirectPhotoUpload={(document, files) => handleFileUpload(files, document)}
                      onError={handleError}
                      disabled={false}
                      dealContact={dealContact}
                      onlyDocumentIds={['seriennummer-ausseneinheit']}
                    />
                    {/* Freie Dateien ergänzen die checklistengebundenen Baustellenfotos und
                        werden im zentralen Google-Drive-Ordner „Baustellendokumentation“ abgelegt. */}
                    <AllgemeineUploads dealId={dealId} dealName={dealName} />
                  </>
                )}
              </div>
            )}

            {/* Auto-Abschluss läuft: Spinner anzeigen während completePhase() verarbeitet */}
            {isDocumentChecklistPhase && completing && (
              <div className="mt-8 flex justify-center items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
                Wird abgeschlossen...
              </div>
            )}
          </>
        )}
      </main>

      {/* CaptureModal für Dokumenten-Upload (HubSpot - alle außer Arbeitsbericht) */}
      {showCaptureModal && (
        <CaptureModal
          onClose={() => {
            setShowCaptureModal(false);
            setSelectedDocument(null);
          }}
          onCapture={handleImageCapture}
          allowedTypes={selectedDocument?.allowedTypes || ['image/jpeg', 'image/png']}
          title={`${selectedDocument?.name || 'Dokument'} hinzufügen`}
          dealName={dealName}
          phaseId={phaseId}
          projectId={projectId}
          checklistItemId={selectedDocument?._id}
        />
      )}

      {/* SeriennummerModal für Seriennummer der Außeneinheit */}
      {showSeriennummerModal && (
        <SeriennummerModal
          onClose={() => {
            setShowSeriennummerModal(false);
            setSelectedDocument(null);
            setReopenRegistrierenAfterAusseneinheit(false);
          }}
          onSubmit={handleSeriennummerSubmit}
          initialValue={seriennummerAusseneinheitInitialValue}
        />
      )}

      {/* SeriennummerRegistrierenModal für Inbetriebnahme-Phase */}
      {showSeriennummerRegistrierenModal && (
        <SeriennummerRegistrierenModal
          onClose={() => {
            setShowSeriennummerRegistrierenModal(false);
            setSelectedDocument(null);
          }}
          onSubmit={handleSeriennummerRegistrierenSubmit}
          onAddPhoto={handleOpenSeriennummerAusseneinheitFromRegistrieren}
          initialValue={seriennummerRegistrierenInitialValue}
        />
      )}

      {/* FileUploadModal für Legacy Multi-Upload (falls benötigt für andere Dokumente) */}
      {showFileUploadModal && selectedDocument && (
        <FileUploadModal
          isOpen={showFileUploadModal}
          onClose={() => {
            setShowFileUploadModal(false);
            setSelectedDocument(null);
          }}
          onUpload={handleFileUpload}
          title={`${selectedDocument.name} hinzufügen`}
          allowedTypes={isDurchfuehrungPhase ? ['image/*'] : selectedDocument.allowedTypes || ['application/pdf']}
          multiUpload={true}
          nativePickerOnly={isDurchfuehrungPhase}
          documentId={selectedDocument._id}
          phaseId={phaseId}
          projectId={projectId}
        />
      )}

      {/* Phasen-Direktnavigation (Issue #16) – sitzt über der globalen BottomNavigation */}
      {phaseId && projectId && allPhases.length > 0 && (
        <PhaseFooterNav
          phases={allPhases}
          currentPhaseId={phaseId}
          projectId={projectId}
        />
      )}

      {/* Countdown-Toast nach Auto-Abschluss */}
      {completionCountdown !== null && (
        <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+80px)] left-0 right-0 flex justify-center z-[60] px-4 sm:bottom-[calc(env(safe-area-inset-bottom)+16px)]">
          <div className="bg-green-500 text-white rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[280px] max-w-[400px] animate-slide-up">
            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <div className="flex-1 text-sm font-medium">
              Phase abgeschlossen! Weiter in {completionCountdown}s...
            </div>
            <button
              onClick={handleCancelCompletion}
              className="flex-shrink-0 bg-white/20 hover:bg-white/30 rounded px-2 py-1 text-xs font-semibold transition-colors"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Sync-Status-Anzeige, Offset passend zur PhaseFooterNav (80px statt globaler 64px BottomNavigation) */}
      <SyncStatus bottomOffsetPx={80} />
    </div>
  );
};

export default PhaseScreen;
